import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import api, { errMsg } from "@/lib/api";
import { useFeatureFlags } from "@/context/FeatureFlagContext";
import { AlertTriangle, Eye, RotateCcw, Save, Search, ShieldCheck, Sparkles } from "lucide-react";

const PORTAL_ORDER = ["admin", "teacher", "parent", "finance", "owner", "public", "advanced"];

export default function FeatureControlPage() {
  const { refresh: refreshFlags } = useFeatureFlags();
  const [portals, setPortals] = useState([]);
  const [pending, setPending] = useState({});  // key -> bool (unsaved override)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("admin");
  const [search, setSearch] = useState("");
  const [confirm, setConfirm] = useState(null);  // { title, message, onConfirm }
  const [preview, setPreview] = useState(null);
  const [audit, setAudit] = useState([]);
  const [previewRole, setPreviewRole] = useState("admin");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: reg }, { data: aud }] = await Promise.all([
        api.get("/feature-flags"),
        api.get("/feature-flags/audit?limit=50"),
      ]);
      setPortals(reg.portals || []);
      setAudit(aud.items || []);
      setPending({});
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const currentPortal = useMemo(
    () => portals.find((p) => p.portal === activeTab),
    [portals, activeTab]
  );

  const filteredFeatures = useMemo(() => {
    if (!currentPortal) return [];
    const q = search.trim().toLowerCase();
    if (!q) return currentPortal.features;
    return currentPortal.features.filter(
      (f) => f.label.toLowerCase().includes(q) ||
             f.key.toLowerCase().includes(q) ||
             (f.description || "").toLowerCase().includes(q)
    );
  }, [currentPortal, search]);

  const groupedByCategory = useMemo(() => {
    const groups = {};
    filteredFeatures.forEach((f) => {
      if (!groups[f.category]) groups[f.category] = [];
      groups[f.category].push(f);
    });
    return groups;
  }, [filteredFeatures]);

  const stateFor = (f) => (pending[f.key] !== undefined ? pending[f.key] : f.enabled);
  const hasUnsaved = Object.keys(pending).length > 0;

  const setToggle = (feature, next) => {
    // Guard core-teacher features with a warning if the toggle is turning OFF
    if (!next && feature.is_core && feature.portal === "teacher") {
      setConfirm({
        title: "Teacher Core Operation",
        message: `"${feature.label}" is part of Teacher Core Operations. Disabling it may increase Admin workload and reduce teacher self-service. Continue?`,
        confirmLabel: "Disable anyway",
        variant: "warning",
        onConfirm: () => applyToggle(feature.key, false),
      });
      return;
    }
    applyToggle(feature.key, next);
  };

  const applyToggle = (key, next) => {
    setPending((prev) => {
      const clone = { ...prev };
      // If it matches the persisted value, drop from pending
      const orig = portals.flatMap((p) => p.features).find((f) => f.key === key);
      if (orig && orig.enabled === next) {
        delete clone[key];
      } else {
        clone[key] = next;
      }
      return clone;
    });
  };

  const tickAll = () => {
    if (!currentPortal) return;
    const next = { ...pending };
    currentPortal.features.forEach((f) => {
      if (!f.enabled) next[f.key] = true;
      else delete next[f.key];
    });
    setPending(next);
  };

  const untickAll = () => {
    if (!currentPortal) return;
    setConfirm({
      title: "Disable all features?",
      message: `You are about to disable all features in "${currentPortal.label}". This will hide features from users but will not delete existing data. Continue?`,
      confirmLabel: "Disable all",
      variant: "destructive",
      onConfirm: () => {
        const next = { ...pending };
        currentPortal.features.forEach((f) => {
          if (f.enabled) next[f.key] = false;
          else delete next[f.key];
        });
        setPending(next);
      },
    });
  };

  const resetToDefault = async () => {
    if (!currentPortal) return;
    setConfirm({
      title: "Reset to default?",
      message: `Reset "${currentPortal.label}" — all features in this tab will be turned ON. Continue?`,
      confirmLabel: "Reset",
      variant: "default",
      onConfirm: async () => {
        try {
          await api.post("/feature-flags/reset", { portal: currentPortal.portal });
          toast.success(`${currentPortal.label} reset to default (all ON)`);
          await load();
          refreshFlags();
        } catch (e) {
          toast.error(errMsg(e));
        }
      },
    });
  };

  const enableCategory = (cat) => {
    const next = { ...pending };
    (groupedByCategory[cat] || []).forEach((f) => {
      if (!f.enabled) next[f.key] = true;
      else delete next[f.key];
    });
    setPending(next);
  };

  const disableCategory = (cat) => {
    setConfirm({
      title: `Disable "${cat}" category?`,
      message: `All features in the "${cat}" category will be hidden from users. Existing data is preserved. Continue?`,
      confirmLabel: "Disable category",
      variant: "destructive",
      onConfirm: () => {
        const next = { ...pending };
        (groupedByCategory[cat] || []).forEach((f) => {
          if (f.enabled) next[f.key] = false;
          else delete next[f.key];
        });
        setPending(next);
      },
    });
  };

  const saveChanges = async () => {
    const updates = Object.entries(pending).map(([key, enabled]) => ({ key, enabled }));
    if (updates.length === 0) {
      toast.info("No changes to save");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/feature-flags/bulk-update", {
        updates, action: "manual", portal: activeTab,
      });
      toast.success(`Saved — ${data.changed} feature(s) updated`);
      await load();
      refreshFlags();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    try {
      const { data } = await api.post("/feature-flags/preview", { role: previewRole });
      setPreview(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const totalPortalDisabled = currentPortal
    ? currentPortal.features.filter((f) => !stateFor(f)).length : 0;

  return (
    <div className="space-y-6" data-testid="feature-control-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-display font-bold flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-secondary" strokeWidth={1.6} />
            Feature Control
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Default: <strong>all features ON</strong>. Untick to hide a feature from the portal —
            data is never deleted, RBAC permissions still apply.
          </p>
        </div>
        {hasUnsaved && (
          <Badge variant="outline" className="border-amber-400 text-amber-700 bg-amber-50"
                 data-testid="unsaved-badge">
            {Object.keys(pending).length} unsaved change(s)
          </Badge>
        )}
      </div>

      {/* Toolbar */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search feature…" value={search}
                     onChange={(e) => setSearch(e.target.value)}
                     className="pl-9" data-testid="feature-search-input" />
            </div>
            <Button variant="outline" size="sm" onClick={tickAll} data-testid="tick-all-btn">
              Tick All
            </Button>
            <Button variant="outline" size="sm" onClick={untickAll} data-testid="untick-all-btn">
              Untick All
            </Button>
            <Button variant="outline" size="sm" onClick={resetToDefault}
                    data-testid="reset-default-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to Default
            </Button>
            <Button variant="outline" size="sm"
                    onClick={() => { setPreviewRole("admin"); setPreview({ visible: [], hidden: [], total_visible: 0, total_hidden: 0 }); }}
                    data-testid="preview-as-role-btn">
              <Eye className="w-3.5 h-3.5 mr-1" /> Preview as Role
            </Button>
            <Button size="sm" disabled={!hasUnsaved || saving} onClick={saveChanges}
                    data-testid="save-changes-btn">
              <Save className="w-3.5 h-3.5 mr-1" />
              {saving ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Portal Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap h-auto">
          {portals.map((p) => {
            const disabledCount = p.features.filter(
              (f) => (pending[f.key] !== undefined ? !pending[f.key] : !f.enabled)
            ).length;
            return (
              <TabsTrigger key={p.portal} value={p.portal}
                           data-testid={`portal-tab-${p.portal}`}>
                {p.label}
                {disabledCount > 0 && (
                  <span className="ml-1.5 text-[10px] rounded-full bg-destructive/15 text-destructive px-1.5 py-0.5">
                    {disabledCount} off
                  </span>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {portals.map((p) => (
          <TabsContent key={p.portal} value={p.portal} className="space-y-4 mt-4">
            {activeTab === p.portal && (
              <div className="text-xs text-muted-foreground">
                {p.features.length} features total · {totalPortalDisabled} currently off
              </div>
            )}

            {Object.entries(groupedByCategory).length === 0 && !loading && (
              <p className="text-center py-8 text-muted-foreground">No matching features.</p>
            )}

            {Object.entries(groupedByCategory).map(([cat, features]) => (
              <Card key={cat} data-testid={`category-card-${cat.toLowerCase().replace(/\s/g, '-')}`}>
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                  <CardTitle className="text-base">{cat}</CardTitle>
                  <div className="flex items-center gap-1.5">
                    <Button variant="ghost" size="sm" className="h-7 text-xs"
                            onClick={() => enableCategory(cat)}
                            data-testid={`enable-cat-${cat.toLowerCase().replace(/\s/g, '-')}`}>
                      Enable
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 text-xs"
                            onClick={() => disableCategory(cat)}
                            data-testid={`disable-cat-${cat.toLowerCase().replace(/\s/g, '-')}`}>
                      Disable
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 pt-0">
                  {features.map((f) => {
                    const enabled = stateFor(f);
                    const changed = pending[f.key] !== undefined;
                    return (
                      <div key={f.key}
                           className={`flex items-start gap-3 p-3 rounded-md border transition-colors ${
                             changed ? "bg-amber-50 border-amber-200"
                                     : enabled ? "bg-white" : "bg-muted/40 border-muted"
                           }`}
                           data-testid={`feature-row-${f.key}`}>
                        <Switch checked={enabled} onCheckedChange={(v) => setToggle(f, v)}
                                data-testid={`feature-toggle-${f.key}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-medium text-sm">{f.label}</p>
                            {enabled
                              ? <Badge variant="outline" className="text-[10px] h-5 border-emerald-300 text-emerald-700 bg-emerald-50">
                                  Enabled
                                </Badge>
                              : <Badge variant="outline" className="text-[10px] h-5 border-slate-300 text-slate-600 bg-slate-50">
                                  Disabled
                                </Badge>}
                            {f.is_core && (
                              <Badge className="text-[10px] h-5 bg-amber-100 text-amber-800 hover:bg-amber-100 border-amber-300"
                                     data-testid={`core-badge-${f.key}`}>
                                <AlertTriangle className="w-3 h-3 mr-0.5" />
                                Core
                              </Badge>
                            )}
                            {changed && (
                              <Badge className="text-[10px] h-5 bg-blue-100 text-blue-700 hover:bg-blue-100">
                                <Sparkles className="w-3 h-3 mr-0.5" /> Unsaved
                              </Badge>
                            )}
                          </div>
                          {f.description && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {f.description}
                            </p>
                          )}
                          <p className="text-[10px] text-muted-foreground/70 font-mono mt-1">
                            {f.key}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        ))}
      </Tabs>

      {/* Audit trail */}
      <Card data-testid="audit-history-card">
        <CardHeader>
          <CardTitle className="text-base">Audit History</CardTitle>
        </CardHeader>
        <CardContent>
          {audit.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No feature-toggle changes yet.
            </p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {audit.map((a) => (
                <div key={a.id} className="text-xs border-l-2 border-secondary pl-3 py-1.5">
                  <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                    <span className="font-semibold">{a.user_email || "system"}</span>
                    <span className="text-muted-foreground">
                      · {a.action}{a.portal ? ` · ${a.portal}` : ""} · {new Date(a.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-muted-foreground mt-0.5">
                    {(a.entries || []).map((e, i) => (
                      <span key={i} className="mr-2">
                        <strong>{e.label || e.key}</strong>: {e.prev ? "ON" : "OFF"} → {e.new ? "ON" : "OFF"}
                        {i < a.entries.length - 1 ? "," : ""}
                      </span>
                    ))}
                  </div>
                  {a.remarks && <div className="italic text-muted-foreground mt-0.5">&ldquo;{a.remarks}&rdquo;</div>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation dialog */}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {confirm?.variant === "warning" && <AlertTriangle className="w-5 h-5 text-amber-500" />}
              {confirm?.title}
            </DialogTitle>
            <DialogDescription>{confirm?.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}
                    data-testid="confirm-cancel-btn">Cancel</Button>
            <Button
              variant={confirm?.variant === "destructive" ? "destructive" : "default"}
              onClick={() => { confirm?.onConfirm?.(); setConfirm(null); }}
              data-testid="confirm-ok-btn">
              {confirm?.confirmLabel || "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview-as-role dialog */}
      <Dialog open={preview !== null} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle>Preview as Role</DialogTitle>
            <DialogDescription>
              Read-only preview — no impersonation. Pick a role to see what they will and won&apos;t see
              after your unsaved changes are saved.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 mb-3">
            <Select value={previewRole} onValueChange={setPreviewRole}>
              <SelectTrigger className="w-[220px]" data-testid="preview-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="super_admin">Super Admin</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="finance">Finance</SelectItem>
                <SelectItem value="teacher">Teacher</SelectItem>
                <SelectItem value="parent">Parent / Student</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" onClick={runPreview} data-testid="preview-run-btn">Run Preview</Button>
          </div>
          {preview && (preview.visible || preview.hidden) && (
            <div className="grid grid-cols-2 gap-4 max-h-[400px] overflow-y-auto">
              <div>
                <p className="font-semibold text-sm mb-2 text-emerald-700">
                  Visible ({preview.total_visible || 0})
                </p>
                <ul className="space-y-1 text-xs">
                  {(preview.visible || []).map((f) => (
                    <li key={f.key} className="text-muted-foreground">✓ {f.label}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="font-semibold text-sm mb-2 text-destructive">
                  Hidden ({preview.total_hidden || 0})
                </p>
                <ul className="space-y-1 text-xs">
                  {(preview.hidden || []).map((f) => (
                    <li key={f.key} className="text-muted-foreground line-through">✗ {f.label}</li>
                  ))}
                  {(!preview.hidden || preview.hidden.length === 0) && (
                    <li className="text-muted-foreground italic">Nothing hidden.</li>
                  )}
                </ul>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)}
                    data-testid="preview-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
